/**
 * AXOIX Pricing Service
 * Fetches pricing data from Admin Mothership API
 * Single source of truth for all pricing displayed on Marketing website
 */

import axios from "axios";

// Admin API URL - Production or relative in development to route via setupProxy.js
const ADMIN_API_URL =
  process.env.NODE_ENV === "development" && typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ? ""
    : (process.env.REACT_APP_API_BASE_URL || "https://api.axoix.in");

// Types matching the API response
export interface PricingFeature {
  text: string;
  included: boolean;
  highlight?: boolean;
}

export interface PricingTierAPI {
  id: string;
  name: string;
  shipName: string;
  description: string;
  monthlyPrice: number | null;
  quarterlyPrice: number | null;
  sixMonthPrice: number | null;
  yearlyPrice: number | null;
  fiveYearPrice: number | null;
  tenYearPrice: number | null;
  originalMonthlyPrice: number | null;
  originalQuarterlyPrice: number | null;
  originalSixMonthPrice: number | null;
  originalYearlyPrice: number | null;
  originalFiveYearPrice: number | null;
  originalTenYearPrice: number | null;
  discountPercent: number;
  currency: string;
  badge: string | null;
  isPopular: boolean;
  isComingSoon: boolean;
  ctaText: string;
  ctaLink: string;
  features: PricingFeature[] | null;
  featureLimits: Record<string, unknown> | null;
  tierLevel: number;
  isActive: boolean;
}

export interface PricingAddonAPI {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  oneTimePrice: number | null;
  currency: string;
  targetIndustries: string[] | null;
  features: string[] | null;
  character: string | null;
  icon: string | null;
  badge: string | null;
  status: string;
  includedInTiers: string[] | null;
  sortOrder: number;
  isActive: boolean;
}

export interface FeatureComparisonAPI {
  id: number;
  category: string;
  featureName: string;
  visitorValue: string | null;
  crewValue: string | null;
  officerValue: string | null;
  commanderValue: string | null;
  titanValue: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface PricingFaqAPI {
  id: number;
  question: string;
  answer: string;
  category: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface CompletePricingData {
  tiers: PricingTierAPI[];
  addons: PricingAddonAPI[];
  featureComparison: FeatureComparisonAPI[];
  faqs: PricingFaqAPI[];
}

// Cache for pricing data
let cachedPricingData: CompletePricingData | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all pricing data from Admin API
 */
export const fetchPricingData = async (): Promise<CompletePricingData> => {
  // Return cached data if still valid
  if (cachedPricingData && Date.now() - cacheTimestamp < CACHE_TTL) {
    return cachedPricingData;
  }

  try {
    const response = await axios.get(`${ADMIN_API_URL}/api/pricing/complete`, {
      timeout: 10000,
      headers: {
        Accept: "application/json",
      },
    });

    if (response.data && response.data.success) {
      // The API wraps everything under `data` ({ success, data: { tiers, addons, ... } }).
      // Read from there; fall back to top-level in case the shape ever changes.
      const payload = response.data.data ?? response.data;

      // The API tier/addon objects don't carry isActive/tierLevel/sortOrder/status the
      // hook filters & sorts on — they use popular/comingSoon/industries instead. Normalize
      // so admin price edits actually surface (otherwise everything filters out → fallback).
      const tiers: PricingTierAPI[] = (payload.tiers || []).map(
        (t: any, i: number): PricingTierAPI => ({
          ...t,
          isActive: t.isActive ?? true,                          // API returns only active tiers
          tierLevel: t.tierLevel ?? i,                           // preserve API order
          isPopular: t.isPopular ?? t.popular ?? false,
          isComingSoon: t.isComingSoon ?? t.comingSoon ?? false,
        })
      );

      // `/api/pricing/complete -> addons[]` returns names but `price: null` for every row.
      // The real prices live in the separate `/api/pricing/usage-packs` endpoint that
      // the admin dashboard reads. Pull from there and map the shape:
      //   { id, name, description, price, currency, quantity, validity_days, resource }
      //   → { id, name, description, monthlyPrice, currency, status: 'active', ... }
      // Fail-soft: if the usage-packs call errors, fall back to the (price-less) addons[]
      // so we don't break the whole pricing page over an addon-side issue.
      let addons: PricingAddonAPI[];
      try {
        const upRes = await axios.get(`${ADMIN_API_URL}/api/pricing/usage-packs`, {
          timeout: 8000,
          headers: { Accept: "application/json" },
        });
        const packs: any[] = Array.isArray(upRes.data)
          ? upRes.data
          : (upRes.data?.usage_packs ?? upRes.data?.data?.usage_packs ?? []);
        addons = packs.map((p: any, i: number): PricingAddonAPI => ({
          id: p.id,
          name: p.name,
          description: p.description ?? "",
          monthlyPrice: p.price ?? null,
          yearlyPrice: p.yearly_price ?? null,
          oneTimePrice: null,
          currency: p.currency ?? "INR",
          targetIndustries: p.target_industries ?? p.industries ?? [],
          features: p.features ?? [],
          character: p.character ?? null,
          icon: p.icon ?? null,
          badge: p.validity_days
            ? `Valid ${p.validity_days} days`
            : (p.billing === "one_time" ? "One-time" : null),
          status: p.is_active === false ? "inactive" : "active",
          includedInTiers: p.included_in_tiers ?? [],
          sortOrder: p.sort_order ?? i,
          isActive: p.is_active ?? true,
        }));
      } catch (upErr) {
        console.warn("[PricingService] /usage-packs failed, falling back to complete.addons:", upErr);
        addons = (payload.addons || []).map(
          (a: any, i: number): PricingAddonAPI => ({
            ...a,
            isActive: a.isActive ?? true,
            sortOrder: a.sortOrder ?? i,
            targetIndustries: a.targetIndustries ?? a.industries ?? [],
            status: a.status ?? (a.comingSoon ? "coming_soon" : "active"),
          })
        );
      }

      cachedPricingData = {
        tiers,
        addons,
        featureComparison: payload.comparison || payload.feature_comparison || [],
        faqs: payload.faqs || [],
      };
      cacheTimestamp = Date.now();
      return cachedPricingData;
    }

    throw new Error("Failed to fetch pricing data");
  } catch (error) {
    console.error("[PricingService] Error fetching pricing:", error);
    // Return cached data even if expired, or throw
    if (cachedPricingData) {
      return cachedPricingData;
    }
    throw error;
  }
};

/**
 * Fetch only pricing tiers
 */
export const fetchPricingTiers = async (): Promise<PricingTierAPI[]> => {
  try {
    const response = await axios.get(`${ADMIN_API_URL}/api/pricing/tiers`, {
      timeout: 10000,
      headers: {
        Accept: "application/json",
      },
    });

    if (response.data && response.data.success) {
      const payload = response.data.data ?? response.data;
      return payload.tiers || [];
    }

    throw new Error("Failed to fetch pricing tiers");
  } catch (error) {
    console.error("[PricingService] Error fetching tiers:", error);
    throw error;
  }
};

/**
 * Fetch only addons
 */
export const fetchPricingAddons = async (): Promise<PricingAddonAPI[]> => {
  try {
    const response = await axios.get(`${ADMIN_API_URL}/api/pricing/addons`, {
      timeout: 10000,
      headers: {
        Accept: "application/json",
      },
    });

    if (response.data && response.data.success) {
      const payload = response.data.data ?? response.data;
      return payload.addons || [];
    }

    throw new Error("Failed to fetch pricing addons");
  } catch (error) {
    console.error("[PricingService] Error fetching addons:", error);
    throw error;
  }
};

/**
 * One industry vertical, priced. `/api/pricing/verticals` is snake_case —
 * it is served straight off `vertical_pricing` rather than through the
 * camelCase mapping the tier/addon endpoints use.
 */
export interface VerticalPricingAPI {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  monthly_price: number;
  yearly_price: number;
  currency: string;
  modules: string[];
  module_count: number;
  table_count: number;
  is_recurring: boolean;
  vertical_limits?: {
    metrics?: Array<{ key: string; label: string; unit: string; overage_key?: string }>;
    tiers?: Record<string, {
      feature_bullets?: string[];
      [key: string]: any;
    }>;
  } | null;
}

let cachedVerticals: VerticalPricingAPI[] | null = null;
let verticalsTimestamp = 0;

/**
 * Fetch industry verticals with their prices.
 *
 * Signup and checkout used to derive this from a hardcoded
 * VERTICAL_PRICING_CONFIG of Rs 10,000 x (1 - 60%) = Rs 4,000, which matched
 * the API only by arithmetic coincidence. The cart total was computed from
 * that constant while the Mothership priced the order from `pricing_tiers`,
 * so any price change would have shown the customer one number and charged
 * another.
 */
export const fetchVerticals = async (): Promise<VerticalPricingAPI[]> => {
  if (cachedVerticals && Date.now() - verticalsTimestamp < CACHE_TTL) {
    return cachedVerticals;
  }

  try {
    const response = await axios.get(`${ADMIN_API_URL}/api/pricing/verticals`, {
      timeout: 10000,
      headers: { Accept: "application/json" },
    });

    if (response.data && response.data.success) {
      const payload = response.data.data ?? response.data;
      const verticals: VerticalPricingAPI[] = payload.verticals || [];
      if (verticals.length > 0) {
        cachedVerticals = verticals;
        verticalsTimestamp = Date.now();
      }
      return verticals;
    }

    throw new Error("Failed to fetch verticals");
  } catch (error) {
    console.error("[PricingService] Error fetching verticals:", error);
    // Serve a stale copy rather than nothing — a price a few minutes old is
    // far better than a checkout that cannot total.
    if (cachedVerticals) return cachedVerticals;
    throw error;
  }
};

/**
 * Clear cached pricing data
 */
export const clearPricingCache = (): void => {
  cachedPricingData = null;
  cacheTimestamp = 0;
  cachedVerticals = null;
  verticalsTimestamp = 0;
};
